import React from 'react'
import { SlidersHorizontal, ChevronDown } from 'lucide-react'
import {
  AnalyticsPeriod,
  AnalyticsSegment,
  UnitStatus,
  ProjectStatus,
  useAnalyticsFilters,
} from '@/contexts/AnalyticsFiltersContext'
import { MultiSelect } from '@/components/shared/MultiSelect'
import { EntityMultiSelect } from '@/components/shared/EntityMultiSelect'
import { useAnalyticsFilterOptions } from '@/hooks/useAnalyticsFilterOptions'

/**
 * Filter bar variants control which filters are shown.
 * - 'full': All filters (default for Overview, Market, Pipeline, Comparables)
 * - 'developer': Developer scorecard — hides Developer filter, shows Broker
 * - 'broker': Broker scorecard — hides Broker filter, shows Developer
 * - 'architect': Architect scorecard — hides Architect filter, shows Developer
 */
export type FilterBarVariant = 'full' | 'developer' | 'broker' | 'architect'

interface FilterBarProps {
  variant?: FilterBarVariant
}

const PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '6m', label: '6 months' },
  { value: '1y', label: '1 year' },
  { value: 'all', label: 'All time' },
]

const SEGMENT_OPTIONS: { value: AnalyticsSegment | ''; label: string }[] = [
  { value: '', label: 'All segments' },
  { value: 'luxury', label: 'Luxury (> €1M)' },
  { value: 'mid', label: 'Mid (€300k – €1M)' },
  { value: 'entry', label: 'Entry (< €300k)' },
]

const STATUS_CHIPS: { value: UnitStatus; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'unknown', label: 'Unknown' },
]

const PROJECT_STATUS_CHIPS: { value: ProjectStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'sold_out', label: 'Sold out' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'inactive', label: 'Inactive' },
]

function NumberInput({
  label,
  value,
  onChange,
  placeholder,
  step,
}: {
  label: string
  value: number | null
  onChange: (v: number | null) => void
  placeholder?: string
  step?: number
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-secondary-500 mb-1">{label}</label>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') return onChange(null)
          const n = Number(raw)
          onChange(Number.isFinite(n) ? n : null)
        }}
        placeholder={placeholder}
        step={step}
        className="w-28 border border-secondary-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  )
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-secondary-500 mb-1">{label}</label>
      <input
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="border border-secondary-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  )
}

export function AnalyticsFiltersBar({ variant = 'full' }: FilterBarProps) {
  const { filters, setFilter, reset } = useAnalyticsFilters()
  const { data: options } = useAnalyticsFilterOptions()
  const [advancedOpen, setAdvancedOpen] = React.useState(false)

  // Determine which filters to show based on variant
  const isScorecard = variant !== 'full'
  const showDeveloperFilter = variant === 'broker' || variant === 'architect'
  const showBrokerFilter = variant === 'developer'
  const showArchitectFilter = false // Never useful in scorecards
  const showDistrictFilter = !isScorecard
  const showPriceRangeFilters = !isScorecard
  const showSqmRangeFilters = !isScorecard
  const showUnitStatusFilter = !isScorecard
  const showDateFilters = !isScorecard

  // "Advanced" means anything that lives inside the collapsible "More filters"
  // panel — only count filters that are actually visible for this variant.
  const hasAdvancedFilters =
    filters.segment !== null ||
    filters.include_outliers ||
    (showDistrictFilter && filters.districts.length > 0) ||
    (showPriceRangeFilters && filters.min_price !== null) ||
    (showPriceRangeFilters && filters.max_price !== null) ||
    (showSqmRangeFilters && filters.min_sqm !== null) ||
    (showSqmRangeFilters && filters.max_sqm !== null) ||
    (showDeveloperFilter && filters.developer_ids.length > 0) ||
    (showBrokerFilter && filters.broker_ids.length > 0) ||
    (showArchitectFilter && filters.architect_ids.length > 0) ||
    (showUnitStatusFilter && filters.status.length > 0) ||
    (showDateFilters && filters.listing_date_from !== null) ||
    (showDateFilters && filters.listing_date_to !== null) ||
    (showDateFilters && filters.sales_date_from !== null) ||
    (showDateFilters && filters.sales_date_to !== null)

  // Auto-expand advanced section if any advanced filter is set (e.g. from URL).
  React.useEffect(() => {
    if (hasAdvancedFilters) setAdvancedOpen(true)
  }, [hasAdvancedFilters])

  const hasNonDefault =
    filters.period !== '90d' ||
    filters.typologies.length > 0 ||
    filters.cities.length > 0 ||
    filters.project_status.length > 0 ||
    hasAdvancedFilters

  const advancedCount =
    (filters.segment !== null ? 1 : 0) +
    (filters.include_outliers ? 1 : 0) +
    (showDistrictFilter ? filters.districts.length : 0) +
    (showPriceRangeFilters && filters.min_price !== null ? 1 : 0) +
    (showPriceRangeFilters && filters.max_price !== null ? 1 : 0) +
    (showSqmRangeFilters && filters.min_sqm !== null ? 1 : 0) +
    (showSqmRangeFilters && filters.max_sqm !== null ? 1 : 0) +
    (showDeveloperFilter ? filters.developer_ids.length : 0) +
    (showBrokerFilter ? filters.broker_ids.length : 0) +
    (showArchitectFilter ? filters.architect_ids.length : 0) +
    (showUnitStatusFilter ? filters.status.length : 0) +
    (showDateFilters && filters.listing_date_from !== null ? 1 : 0) +
    (showDateFilters && filters.listing_date_to !== null ? 1 : 0) +
    (showDateFilters && filters.sales_date_from !== null ? 1 : 0) +
    (showDateFilters && filters.sales_date_to !== null ? 1 : 0)

  function toggleStatus(value: UnitStatus) {
    if (filters.status.includes(value)) {
      setFilter(
        'status',
        filters.status.filter((s) => s !== value),
      )
    } else {
      setFilter('status', [...filters.status, value])
    }
  }

  function toggleProjectStatus(value: ProjectStatus) {
    if (filters.project_status.includes(value)) {
      setFilter(
        'project_status',
        filters.project_status.filter((s) => s !== value),
      )
    } else {
      setFilter('project_status', [...filters.project_status, value])
    }
  }

  return (
    <div className="card">
      {/* Primary row — the four filters Hugo & the analyst team touch every session */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-secondary-500 mb-1">Period</label>
          <select
            value={filters.period}
            onChange={(e) => setFilter('period', e.target.value as AnalyticsPeriod)}
            className="border border-secondary-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <MultiSelect
          label="Typology"
          options={options?.typologies ?? []}
          selected={filters.typologies}
          onChange={(next) => setFilter('typologies', next)}
          placeholder="All typologies"
        />

        <MultiSelect
          label="City"
          options={options?.cities ?? []}
          selected={filters.cities}
          onChange={(next) => setFilter('cities', next)}
          placeholder="All cities"
        />

        <div>
          <label className="block text-xs font-medium text-secondary-500 mb-1">Project status</label>
          <div className="flex flex-wrap gap-1.5">
            {PROJECT_STATUS_CHIPS.map((s) => {
              const active = filters.project_status.includes(s.value)
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleProjectStatus(s.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    active
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-secondary-700 border-secondary-200 hover:border-secondary-300'
                  }`}
                >
                  {s.label}
                </button>
              )
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm text-secondary-600 hover:text-secondary-900 mb-1.5"
          aria-expanded={advancedOpen}
        >
          <SlidersHorizontal className="h-4 w-4" />
          More filters
          {advancedCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-xs font-medium px-1.5 min-w-[1.25rem] h-5">
              {advancedCount}
            </span>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
        </button>

        {hasNonDefault && (
          <button
            type="button"
            onClick={reset}
            className="ml-auto text-sm text-primary-600 hover:text-primary-800 underline mb-1.5"
          >
            Reset filters
          </button>
        )}
      </div>

      {advancedOpen && (
        <div className="mt-4 pt-4 border-t border-secondary-100 space-y-4">
          {/* Row A — pricing shape: segment shortcut, outlier toggle, district, price/m² ranges */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-secondary-500 mb-1">Segment</label>
              <select
                value={filters.segment ?? ''}
                onChange={(e) =>
                  setFilter('segment', e.target.value === '' ? null : (e.target.value as AnalyticsSegment))
                }
                className="border border-secondary-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {SEGMENT_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-secondary-600 mb-1.5">
              <input
                type="checkbox"
                checked={filters.include_outliers}
                onChange={(e) => setFilter('include_outliers', e.target.checked)}
              />
              Include €/m² outliers
            </label>

            {showDistrictFilter && (
              <MultiSelect
                label="District"
                options={options?.districts ?? []}
                selected={filters.districts}
                onChange={(next) => setFilter('districts', next)}
                placeholder="All districts"
              />
            )}

            {showPriceRangeFilters && (
              <div className="flex items-end gap-2">
                <NumberInput
                  label="Min €"
                  value={filters.min_price}
                  onChange={(v) => setFilter('min_price', v)}
                  placeholder="300000"
                  step={1000}
                />
                <NumberInput
                  label="Max €"
                  value={filters.max_price}
                  onChange={(v) => setFilter('max_price', v)}
                  placeholder="1500000"
                  step={1000}
                />
              </div>
            )}

            {showSqmRangeFilters && (
              <div className="flex items-end gap-2">
                <NumberInput
                  label="Min m²"
                  value={filters.min_sqm}
                  onChange={(v) => setFilter('min_sqm', v)}
                  placeholder="50"
                  step={5}
                />
                <NumberInput
                  label="Max m²"
                  value={filters.max_sqm}
                  onChange={(v) => setFilter('max_sqm', v)}
                  placeholder="250"
                  step={5}
                />
              </div>
            )}
          </div>

          {/* Row B — entities (developer / broker / architect) — only if any entity filter is visible */}
          {(showDeveloperFilter || showBrokerFilter || showArchitectFilter) && (
            <div className="flex flex-wrap items-end gap-4">
              {showDeveloperFilter && (
                <EntityMultiSelect
                  label="Developer"
                  options={options?.developers ?? []}
                  selected={filters.developer_ids}
                  onChange={(next) => setFilter('developer_ids', next)}
                  placeholder="All developers"
                />
              )}
              {showBrokerFilter && (
                <EntityMultiSelect
                  label="Broker"
                  options={options?.brokers ?? []}
                  selected={filters.broker_ids}
                  onChange={(next) => setFilter('broker_ids', next)}
                  placeholder="All brokers"
                />
              )}
              {showArchitectFilter && (
                <EntityMultiSelect
                  label="Architect"
                  options={options?.architects ?? []}
                  selected={filters.architect_ids}
                  onChange={(next) => setFilter('architect_ids', next)}
                  placeholder="All architects"
                />
              )}
            </div>
          )}

          {/* Row C — unit status + date ranges (only for full variant) */}
          {(showUnitStatusFilter || showDateFilters) && (
            <div className="flex flex-wrap items-end gap-4">
              {showUnitStatusFilter && (
                <div>
                  <label className="block text-xs font-medium text-secondary-500 mb-1">Unit status</label>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS_CHIPS.map((s) => {
                      const active = filters.status.includes(s.value)
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => toggleStatus(s.value)}
                          className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                            active
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-secondary-700 border-secondary-200 hover:border-secondary-300'
                          }`}
                        >
                          {s.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {showDateFilters && (
                <>
                  <div className="flex items-end gap-2">
                    <DateInput
                      label="Listed from"
                      value={filters.listing_date_from}
                      onChange={(v) => setFilter('listing_date_from', v)}
                    />
                    <DateInput
                      label="Listed to"
                      value={filters.listing_date_to}
                      onChange={(v) => setFilter('listing_date_to', v)}
                    />
                  </div>

                  <div className="flex items-end gap-2">
                    <DateInput
                      label="Sold from"
                      value={filters.sales_date_from}
                      onChange={(v) => setFilter('sales_date_from', v)}
                    />
                    <DateInput
                      label="Sold to"
                      value={filters.sales_date_to}
                      onChange={(v) => setFilter('sales_date_to', v)}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
